import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function SettingsPage() {
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState({
    new_game_notifications: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [ratingParticipation, setRatingParticipation] = useState(true);
  const [savingParticipation, setSavingParticipation] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPreferences();
    fetchRatingParticipation();
  }, []);

  const fetchRatingParticipation = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await fetch(`${backendUrl}/api/user/rating-participation`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRatingParticipation(data.enabled !== false);
      }
    } catch (error) {
      console.error('Error fetching rating participation:', error);
    }
  };

  const saveRatingParticipation = async (enabled) => {
    setSavingParticipation(true);
    setMessage({ type: '', text: '' });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/user/rating-participation`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ enabled })
      });
      if (response.ok) {
        setRatingParticipation(enabled);
        setMessage({
          type: 'success',
          text: enabled
            ? 'Rating participation enabled. Your rating, kudos and leaderboard visibility are restored.'
            : 'Rating participation disabled. Your rating and kudos are now hidden from other players.'
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      } else {
        setMessage({ type: 'error', text: 'Failed to update rating participation. Please try again.' });
      }
    } catch (error) {
      console.error('Error saving rating participation:', error);
      setMessage({ type: 'error', text: 'Network error. Rating participation was not changed.' });
    } finally {
      setSavingParticipation(false);
      setShowDisableConfirm(false);
    }
  };

  const handleParticipationToggle = () => {
    if (ratingParticipation) {
      setShowDisableConfirm(true);
    } else {
      saveRatingParticipation(true);
    }
  };

  const fetchPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${backendUrl}/api/user/notification-preferences`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPreferences(data);
      } else if (response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        // If backend fails, use default preferences (all enabled)
        console.warn('Failed to load preferences from backend, using defaults');
        setPreferences({ new_game_notifications: true });
        setMessage({ 
          type: 'error', 
          text: 'Could not load preferences from server. Using default settings.' 
        });
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      // On network error, use default preferences (all enabled)
      setPreferences({ new_game_notifications: true });
      setMessage({ 
        type: 'error', 
        text: 'Network error. Using default settings. Your preferences will be saved when the connection is restored.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key) => {
    const newPreferences = {
      ...preferences,
      [key]: !preferences[key]
    };

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/user/notification-preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newPreferences)
      });

      if (response.ok) {
        setPreferences(newPreferences);
        setMessage({ 
          type: 'success', 
          text: 'Preferences saved successfully!' 
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        setMessage({ type: 'error', text: 'Failed to save preferences' });
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      setMessage({ type: 'error', text: 'Failed to save preferences' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">⚙️ Settings</h1>
          <p className="text-gray-600">Manage your account preferences and notifications</p>
        </div>

        {/* Success/Error Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <div className="flex items-center gap-2">
              {message.type === 'success' ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              <span className="font-medium">{message.text}</span>
            </div>
          </div>
        )}

        {/* Notification Preferences Section */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">📧 Email Notifications</h2>
          <p className="text-gray-600 mb-6">
            Choose which email notifications you'd like to receive.
          </p>

          <div className="space-y-4">
            {/* New Game Notifications */}
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl">🏐</span>
                  <h3 className="font-semibold text-gray-800">New Game Notifications</h3>
                </div>
                <p className="text-sm text-gray-600 ml-7">
                  Receive emails when new volleyball games are created or existing games are updated
                </p>
              </div>
              <button
                onClick={() => handleToggle('new_game_notifications')}
                disabled={saving}
                className={`relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                  preferences.new_game_notifications ? 'bg-orange-600' : 'bg-gray-200'
                } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                role="switch"
                aria-checked={preferences.new_game_notifications}
              >
                <span
                  className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    preferences.new_game_notifications ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Rating Participation Section */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">⭐ Rating participation</h2>
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-testid="rating-participation-section">
              <div className="flex-1 pr-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl">🏅</span>
                  <h3 className="font-semibold text-gray-800">Participate in player ratings</h3>
                </div>
                <p className="text-sm text-gray-600 ml-7">
                  Enable rating participation to rate other players and to be rated after games.
                  If disabled, your existing rating and kudos are kept privately, visible only to admins,
                  and you will not appear on rating leaderboards.
                </p>
              </div>
              <button
                onClick={handleParticipationToggle}
                disabled={savingParticipation}
                data-testid="rating-participation-toggle"
                className={`relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                  ratingParticipation ? 'bg-orange-600' : 'bg-gray-200'
                } ${savingParticipation ? 'opacity-50 cursor-not-allowed' : ''}`}
                role="switch"
                aria-checked={ratingParticipation}
                aria-label="Rating participation"
              >
                <span
                  className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    ratingParticipation ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            {!ratingParticipation && (
              <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600" data-testid="rating-participation-disabled-info">
                Rating participation is currently disabled. You can't rate players and other players can't rate you.
                Your historical ratings and kudos are safely preserved and will become visible again if you re-enable participation.
              </div>
            )}
          </div>

          {/* Future Settings Placeholder */}
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="font-semibold text-blue-800 mb-1">More Settings Coming Soon</h4>
                <p className="text-sm text-blue-700">
                  Additional notification preferences and account settings will be available in future updates.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Disable Rating Participation Confirmation Dialog */}
        {showDisableConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" data-testid="rating-participation-confirm-dialog">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Disable rating participation?</h3>
              <p className="text-sm text-gray-600 mb-3">If you disable rating participation:</p>
              <ul className="text-sm text-gray-700 space-y-2 mb-4 list-disc pl-5">
                <li>You will no longer be able to rate players after games.</li>
                <li>Other players will no longer be able to rate you.</li>
                <li>Your existing rating and kudos will be hidden from players and removed from leaderboards.</li>
                <li>You will be treated as rating 0 for rating-prioritized games.</li>
                <li>Your historical data is preserved and becomes visible again if you enable participation later.</li>
              </ul>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDisableConfirm(false)}
                  disabled={savingParticipation}
                  data-testid="cancel-disable-rating-participation-btn"
                  className="px-4 py-2 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => saveRatingParticipation(false)}
                  disabled={savingParticipation}
                  data-testid="confirm-disable-rating-participation-btn"
                  className="px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {savingParticipation ? 'Disabling...' : 'Disable participation'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Back Button */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-white text-orange-600 border-2 border-orange-600 rounded-lg hover:bg-orange-50 font-semibold transition-colors"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}
